import { ChangeDetectionStrategy, Component, OnDestroy, Optional } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { FormComponent, MessageService } from '@spartacus/organization/administration/components';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { UigBranchesItemService } from '../../services/uig-branches-item.service';
import { UigBranchesListService } from '../../services/uig-branches-list.service';
import { createOrUpdateBranchFailure, createOrUpdateBranchSuccess, selectUigStoreFeatureGroups } from '../../store/uig-branches.selectors';
import { UigBranchesState } from '../../store/uig-branches.reducer';
import { BranchForm, StoreLocatorFeatureGroup } from '../uig-branches.models';
import { createOrUpdateBranchReset, getUigBranchesOverview, getUigStoreFeatureGroups } from '../../store/uig-branches.actions';
import { UigBranchFormService } from '../../services/uig-branch-form.service';
import { GlobalMessageService, GlobalMessageType, RoutingService, TranslationService } from '@spartacus/core';
import { days, supportedCountryCodes } from '../uig-branches';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { UigB2BUnit, UigB2BUser } from '../../uig-user/uig-user.model';
import { OrgUnitService } from '@spartacus/organization/administration/core';

@Component({
    selector: 'app-uig-branch-form',
    templateUrl: './uig-branch-form.component.html',
    styleUrls: ['./uig-branch-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'content-wrapper' },
    standalone: false
})
export class UigBranchFormComponent extends FormComponent<BranchForm> implements OnDestroy {

  i18nRoot: string;
  isCreate: boolean = false;
  form: UntypedFormGroup;
  includedFeatures: String[];
  storeFeatureGroups: StoreLocatorFeatureGroup[];
  supportedCountryCodes: String[] = supportedCountryCodes;
  selectedCountryCode: string = 'DE';
  days: String[] = days;
  createOrUpdateBranchSuccess$: Observable<boolean> = this.store.select(
    createOrUpdateBranchSuccess
  );
  createOrUpdateBranchFailure$: Observable<boolean> = this.store.select(
    createOrUpdateBranchFailure
  );
  storeFeatureGroups$: Observable<StoreLocatorFeatureGroup[]> =
    this.store.select(selectUigStoreFeatureGroups);
  isLoading$ = new BehaviorSubject(true);
  createOrUpdateBranchSuccess: Subscription;
  createOrUpdateBranchFailure: Subscription;
  branchName: string;
  branchVatValidationSubscription: Subscription;
  isVendorReportingRelevant = undefined;
  isB2bUnitVendorReporting$ = this.userService.get().pipe(
      filter((user: UigB2BUser) => user?.orgUnit !== undefined),
      switchMap((user) => this.orgUnitService.get(user?.orgUnit?.uid)),
      map ((unit: UigB2BUnit) => unit.vendorReporting)
    )

  override form$: Observable<UntypedFormGroup | null> = this.itemService.current$.pipe(
      map((item) => {
        this.setI18nRoot(item);
        if (!item) {
          // we trick the form builder...
          item = {} as any;
        }
        return this.itemService.getForm(item);
      }),tap(()=>{
        this.validateVendorReportingRelatedfields();
      })
    );

  constructor(
    @Optional() protected messageService: MessageService,
    protected itemService: UigBranchesItemService,
    protected route: ActivatedRoute,
    protected store: Store<UigBranchesState>,
    protected listService: UigBranchesListService,
    protected formService: UigBranchFormService,
    private translation: TranslationService,
    private globalMessageService: GlobalMessageService,
    protected routingService: RoutingService,
    protected userService: UserAccountFacade,
    protected orgUnitService: OrgUnitService
  ) {
    super(itemService, messageService);
    this.getFeatureGroups();
    this.fillI18nRoot();
    if (!this.isCreate) {
      this.itemService.current$.pipe(
        filter((item) => item != null),
        tap((item) => {
          this.includedFeatures = item.features;
          this.selectedCountryCode = item.countryCode;
          this.isVendorReportingRelevant = item.branchVendorReporting;
        })
      )
        .subscribe();
    }

    this.handleBranchCreation();
  }

  fillI18nRoot() {
    this.route.data.subscribe(data => {
      this.i18nRoot = data['i18nRoot'];
      if (this.i18nRoot == 'branchesCreate') {
        this.isCreate = true;
      }
    });
  }

  save(form: UntypedFormGroup): void {
    if (!form.touched && !form.valid && this.isCreate) {
      form.get('name').markAsTouched();
      form.get('branchId').markAsTouched();
    }

    const isRequiredFieldsValid = form.get('name').valid && form.get('branchId').valid && this.getVendorReportingRelatedControls().every(control => control.valid)

    if (isRequiredFieldsValid) {
      this.branchName = form.get('name').value;
      if (this.isCreate) {
        this.fillBooleans(form);
      }
      form.disable();
      this.itemService.createOrUpdateBranch((form.value as BranchForm));
    }

  }

  private validateVendorReportingRelatedfields() {
    let vendorReportingrelatedControls = this.getVendorReportingRelatedControls();
    if(!this.isCreate && this.form.get('branchVendorReporting').value) {
      this.addFormcontrolsValidation(vendorReportingrelatedControls);
    }
    this.branchVatValidationSubscription = this.form.get('branchVendorReporting')?.valueChanges.subscribe((value) => {
      if (value) {
        this.addFormcontrolsValidation(vendorReportingrelatedControls);
      } else {
        this.clearFormcontrolsValidation(vendorReportingrelatedControls);
      }
    });
  }

  private getVendorReportingRelatedControls() {
    return [
      this.form.get('branchVat'),
      this.form.get('place'),
      this.form.get('postalCode'),
      this.form.get('countryCode'),
      this.form.get('streetAndHouseNo')
    ]
  }

  private addFormcontrolsValidation(controls: AbstractControl<any, any>[]) {
    controls.forEach(control  => {
      control.addValidators(Validators.required);
      control.updateValueAndValidity();
      control.markAsTouched();
    });
  }

  private clearFormcontrolsValidation(controls: AbstractControl<any, any>[]) {
    controls.forEach(control => {
      control.clearValidators();
      control.updateValueAndValidity();
      control.markAsTouched();
    });
  }

  handleBranchCreation() {
    this.createOrUpdateBranchFailure = this.createOrUpdateBranchFailure$
      .pipe(
        filter((error) => error && error === true),
        switchMap(() =>
          this.translation.translate(this.i18nRoot + '.createFailure')
        ),
        tap((text) => {
          this.globalMessageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(createOrUpdateBranchReset());
          this.form.enable();
        })
      )
      .subscribe();
    this.createOrUpdateBranchSuccess = this.createOrUpdateBranchSuccess$
      .pipe(
        filter((success) => success && success === true),
        switchMap(() =>
          this.translation.translate(this.i18nRoot + '.createSuccess', { branchName: this.branchName })
        ),
        tap((text) => {
          this.globalMessageService.add(
            text,
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.store.dispatch(createOrUpdateBranchReset());
          this.store.dispatch(getUigBranchesOverview({ params: null }));
          this.routingService.go({ cxRoute: 'branches' });
        })
      )
      .subscribe();
  }

  getFeatureGroups() {
    this.store.dispatch(getUigStoreFeatureGroups());
    this.storeFeatureGroups$
      .pipe(
        filter((storeFeatureGroups) => storeFeatureGroups != null),
        tap((storeFeatureGroups) => {
          this.storeFeatureGroups = storeFeatureGroups;
          this.form = this.itemService.getForm(undefined, this.storeFeatureGroups);
        })
      )
      .subscribe(() => {
        this.isLoading$.next(false);
      });
  }

  fillBooleans(form: UntypedFormGroup) {
    if (!form.get('headOffice').value) {
      form.get('headOffice').setValue(false);
    }
    if (!form.get('onlineShop').value) {
      form.get('onlineShop').setValue(false);
    }
  }

  checkboxChanged(event, featureKey: string) {
    if (event.target.checked) {
      for (let control of (this.form.get('features') as UntypedFormArray).controls) {
        if (control.value === null) {
          control.setValue(featureKey);
          break;
        }
      }
    }
    else {
      for (let control of (this.form.get('features') as UntypedFormArray).controls) {
        if (control.value === featureKey) {
          control.setValue(null);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.createOrUpdateBranchFailure.unsubscribe();
    this.createOrUpdateBranchSuccess.unsubscribe();
    this.branchVatValidationSubscription.unsubscribe();
    this.itemService.setEditMode(false);
  }
}

